import { useEffect, useMemo, useState } from 'react';
import { testPresetRange, validatePresetContent, type CabinetInstance, type ParametricPresetContent, type PresetContent, type ProjectDocument } from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import { Api } from '../lib/api';
import { navigate } from '../lib/router';
import { t } from '../lib/i18n';
import { Logo } from '../lib/ui';
import { TopbarUser } from '../App';
import { CATEGORY_LABEL, usePresets } from '../editor/LibraryPanel';
import { CabinetProperties } from '../editor/Properties';
import { loadVersion } from '../editor/presets';

const SCOPE_LABEL = { system: 'Sistemski', workspace: 'Radionica', private: 'Lični' } as const;

export function PresetsPage() {
  const [key, setKey] = useState(0);
  const { items, error } = usePresets(key);
  const [selected, setSelected] = useState<string | null>(null);
  const current = items?.find((p) => p.id === selected) ?? null;
  return (
    <div>
      <header className="topbar">
        <button className="btn ghost sm" onClick={() => navigate('/')}>
          <Logo size={18} /> ←
        </button>
        <strong>Preseti</strong>
        <div className="spacer" />
        <TopbarUser />
      </header>
      <main className="dash" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)', gap: 16 }}>
        <section className="card" style={{ padding: 8, boxShadow: 'none', alignSelf: 'start' }}>
          {error && <p className="error-text">{error}</p>}
          {items?.map((p) => (
            <button key={p.id} className={`btn ghost ${selected === p.id ? 'on' : ''}`} style={{ width: '100%', justifyContent: 'space-between', marginBottom: 2 }} onClick={() => setSelected(p.id)}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
              <span className="faint" style={{ fontSize: 11 }}>
                {SCOPE_LABEL[p.scope]} v{p.latestVersion}
              </span>
            </button>
          ))}
        </section>
        <section style={{ minWidth: 0 }}>
          {current ? <PresetDetail key={`${current.id}:${current.latestVersion}`} preset={current} onChanged={(id) => { setKey((k) => k + 1); if (id) setSelected(id); }} /> : <p className="muted">Izaberite preset sa liste.</p>}
        </section>
      </main>
    </div>
  );
}

function PresetDetail({ preset, onChanged }: { preset: PresetSummaryDto; onChanged(newId?: string): void }) {
  const [versions, setVersions] = useState<{ id: string; version: number; message: string | null; createdAt: string }[]>([]);
  const [content, setContent] = useState<PresetContent | null>(null);
  const [draft, setDraft] = useState<ParametricPresetContent | null>(null);
  const [meta, setMeta] = useState({ name: preset.name, category: preset.category, description: preset.description, tags: preset.tags.join(', ') });
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    Api.preset(preset.id).then((d) => setVersions(d.versions));
    loadVersion(preset.latestVersionId).then((c) => {
      setContent(c);
      if (c.kind === 'parametric') setDraft(structuredClone(c));
    });
  }, [preset.id, preset.latestVersionId]);

  const cases = useMemo(() => (draft ? testPresetRange(draft) : []), [draft]);
  const problems = useMemo(() => (draft ? validatePresetContent(draft) : []), [draft]);
  const changed = draft && content && JSON.stringify(draft) !== JSON.stringify(content);
  const invalid = problems.length > 0 || cases.some((c) => c.errors.length > 0);

  // Base params are edited with the same property panel as in the editor, on a throw-away instance.
  const fakeInst: CabinetInstance | null = draft
    ? { id: 'preset', name: preset.name, params: draft.base, transform: { position: [0, 0, 0], rotationY: 0, mirrored: false }, materials: {}, edgeOverrides: {}, hidden: false, locked: false, environment: 'dry' as const, preset: null }
    : null;
  const execBase = (_label: string, mutate: (d: ProjectDocument) => void) => {
    if (!draft || !fakeInst) return;
    const d = { cabinets: [structuredClone(fakeInst)], assemblies: [] } as unknown as ProjectDocument;
    mutate(d);
    const base = d.cabinets[0]!.params;
    // Keep exposed defaults in sync with the edited base.
    const params = draft.params.map((p) => {
      const v = p.path.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown>)?.[k], base);
      return typeof v === 'number' ? { ...p, default: v } : p;
    });
    setDraft({ ...draft, base, params });
  };

  const save = async (mode: 'version' | 'copy') => {
    if (!draft && mode === 'version') return;
    setStatus(null);
    try {
      if (mode === 'version') {
        await Api.newPresetVersion(preset.id, draft!, message || undefined);
        setStatus({ ok: true, text: 'Nova verzija snimljena. Postojeći projekti ostaju na svojoj verziji.' });
        onChanged();
      } else {
        const created = await Api.createPreset({
          name: `${meta.name} (kopija)`.slice(0, 120),
          category: meta.category,
          scope: 'private',
          description: meta.description,
          tags: meta.tags.split(',').map((s) => s.trim()).filter(Boolean),
          content: (draft ?? content)!,
          message: `Snimljeno kao kopija od ${preset.name} v${preset.latestVersion}`,
        });
        setStatus({ ok: true, text: `Kreiran lični preset "${created.name}".` });
        onChanged(created.id);
      }
    } catch (e) {
      const err = e as Error & { body?: { errors?: string[] } };
      setStatus({ ok: false, text: [err.message, ...(err.body?.errors ?? [])].join(' ') });
    }
  };

  return (
    <div className="card" style={{ padding: 16, boxShadow: 'none' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>{preset.name}</h2>
        <span className="badge">{SCOPE_LABEL[preset.scope]}</span>
        <span className="badge">v{preset.latestVersion}</span>
        <span className="badge">{preset.kind === 'assembly' ? 'statički sklop' : 'parametarski'}</span>
      </div>
      <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>{preset.description}</p>
      {!preset.canEdit && (
        <p className="faint" style={{ fontSize: 12 }}>
          {preset.scope === 'system' ? 'Sistemski preset mijenja samo administrator. Izmjene snimite kao ličnu kopiju.' : 'Nemate pravo izmjene ovog preseta.'}
        </p>
      )}

      {preset.canEdit && preset.scope !== 'system' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <label className="field">
            <span>Naziv</span>
            <input className="input" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
          </label>
          <label className="field">
            <span>Kategorija</span>
            <select className="input" value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value as PresetSummaryDto['category'] })}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ gridColumn: '1 / 3' }}>
            <span>Opis</span>
            <textarea className="input" style={{ height: 50, padding: 6 }} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
          </label>
          <label className="field">
            <span>Tagovi</span>
            <input className="input" value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })} />
          </label>
          <div style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
            <button
              className="btn"
              onClick={async () => {
                try {
                  await Api.patchPreset(preset.id, { name: meta.name, category: meta.category, description: meta.description, tags: meta.tags.split(',').map((s) => s.trim()).filter(Boolean) });
                  onChanged();
                } catch (e) {
                  setStatus({ ok: false, text: (e as Error).message });
                }
              }}
            >
              Snimi opis
            </button>
            <button
              className="btn danger"
              onClick={async () => {
                if (!confirm('Arhivirati preset? Projekti koji ga koriste zadržavaju svoju verziju.')) return;
                await Api.archivePreset(preset.id);
                onChanged();
              }}
            >
              Arhiviraj
            </button>
          </div>
        </div>
      )}

      {draft && (
        <>
          <h3 style={{ marginTop: 16 }}>Izloženi parametri</h3>
          <table className="grid">
            <thead>
              <tr>
                <th>Ključ</th>
                <th>Naziv</th>
                <th>Putanja</th>
                <th>Default</th>
                <th>Min</th>
                <th>Max</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {draft.params.map((p, i) => {
                const scale = p.dim === 'length' ? 1000 : 1;
                const setNum = (field: 'default' | 'min' | 'max', raw: string) => {
                  const n = Math.round(Number(raw.replace(',', '.')) * scale);
                  if (!Number.isFinite(n)) return;
                  setDraft({ ...draft, params: draft.params.map((x, j) => (j === i ? { ...x, [field]: n } : x)) });
                };
                return (
                  <tr key={p.key}>
                    <td className="mono">{p.key}</td>
                    <td>
                      <input className="input" style={{ height: 24 }} value={p.label} onChange={(e) => setDraft({ ...draft, params: draft.params.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                    </td>
                    <td className="mono faint">{p.path}</td>
                    {(['default', 'min', 'max'] as const).map((f) => (
                      <td key={f}>
                        <input className="input" style={{ height: 24, width: 80 }} defaultValue={String(p[f] / scale)} onBlur={(e) => setNum(f, e.target.value)} aria-label={`${p.label} ${f}`} />
                      </td>
                    ))}
                    <td>
                      <button className="btn sm ghost danger" onClick={() => setDraft({ ...draft, params: draft.params.filter((_, j) => j !== i) })} title="Ukloni parametar">
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <h3 style={{ marginTop: 16 }}>Osnovna konfiguracija</h3>
          <div className="card" style={{ boxShadow: 'none', maxWidth: 420 }}>
            {fakeInst && <CabinetProperties inst={fakeInst} exec={execBase as never} disabled={false} />}
          </div>
          <h3 style={{ marginTop: 16 }}>Provjera min/default/max</h3>
          {problems.map((p) => (
            <p key={p} className="error-text">
              {p}
            </p>
          ))}
          <table className="grid">
            <tbody>
              {cases.map((c) => (
                <tr key={c.label}>
                  <td style={{ width: 220 }}>{c.label}</td>
                  <td className={c.errors.length ? 'case-err' : 'case-ok'} style={{ whiteSpace: 'normal' }}>
                    {c.errors.length ? c.errors.slice(0, 3).join(' ') : 'Ispravno'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {content?.kind === 'assembly' && (
        <>
          <h3 style={{ marginTop: 16 }}>Ploče sklopa ({content.panels.length})</h3>
          <p className="faint" style={{ fontSize: 12 }}>
            Statički sklop: mjere ploča se mijenjaju u editoru (ubacite preset, izmijenite, pa „Preset iz selekcije” za novu verziju ili novi preset).
          </p>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {preset.canEdit && draft && (
          <>
            <input className="input" placeholder="Opis izmjene (opciono)" value={message} onChange={(e) => setMessage(e.target.value)} style={{ minWidth: 220 }} />
            <button className="btn primary" disabled={!changed || invalid} onClick={() => save('version')} title={invalid ? 'Ispravite nevažeće konfiguracije' : undefined}>
              Snimi novu verziju (v{preset.latestVersion + 1})
            </button>
          </>
        )}
        <button className="btn" disabled={invalid} onClick={() => save('copy')}>
          Snimi kao lični preset
        </button>
      </div>
      {status && <p className={status.ok ? 'ok-text' : 'error-text'}>{status.text}</p>}

      <h3 style={{ marginTop: 18 }}>Verzije</h3>
      <table className="grid">
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <td style={{ width: 60 }}>v{v.version}</td>
              <td>{v.message ?? ''}</td>
              <td className="faint">{new Date(v.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="faint" style={{ fontSize: 11 }}>{t('dash.revision')}: verzije su nepromjenjive; projekti pokazuju na verziju koju su ubacili dok ih ne ažurirate.</p>
    </div>
  );
}
